import type { RevenueGoalResponse } from '@sea-snag/shared';

import type { BoatRevenueBreakdown } from './api-client';

/** Goals: where each boat stands against its goal, and editing those goals. */

export type GoalStatus = 'no-goal' | 'at-risk' | 'on-track' | 'ahead';

/**
 * Where a boat stands against its revenue target, compared with how far into
 * the season we are: more than 10 points behind is at risk, more than 10 ahead
 * is ahead. A target of 0 counts as no goal.
 */
export function goalStatus(
  boat: Pick<BoatRevenueBreakdown, 'totalRevenue' | 'targetRevenue' | 'expectedProgressPct'>
): GoalStatus {
  const target = boat.targetRevenue;
  if (target === null || target <= 0) return 'no-goal';
  const delta = boat.expectedProgressPct - (boat.totalRevenue / target) * 100;
  if (delta > 10) return 'at-risk';
  if (delta < -10) return 'ahead';
  return 'on-track';
}

/** One boat's goal while it's edited; the numbers stay strings until saved. */
export interface GoalDraft {
  boatPid: string;
  boatName: string;
  boatModel: string | null;
  currency: string;
  targetRevenue: string;
  targetWeeks: string;
  notes: string;
  hasGoal: boolean;
  dirty: boolean;
}

/**
 * An edit row for every boat, filled from its saved goal. A row being edited is
 * kept as it is, so a refetch in the background or a partly failed save never
 * wipes unsaved input.
 */
export function buildGoalDrafts(
  boats: Pick<BoatRevenueBreakdown, 'boatPid' | 'boatName' | 'model' | 'currency'>[],
  goals: Pick<
    RevenueGoalResponse,
    'marketBoatId' | 'targetRevenue' | 'targetWeeks' | 'currency' | 'notes'
  >[],
  prev: Record<string, GoalDraft>
): Record<string, GoalDraft> {
  const goalByBoat = new Map(goals.map((g) => [g.marketBoatId, g]));
  const next: Record<string, GoalDraft> = {};
  for (const boat of boats) {
    const existing = prev[boat.boatPid];
    if (existing?.dirty) {
      next[boat.boatPid] = existing;
      continue;
    }
    const goal = goalByBoat.get(boat.boatPid);
    // Single-currency v1; see #89 for multi-currency support.
    next[boat.boatPid] = {
      boatPid: boat.boatPid,
      boatName: boat.boatName,
      boatModel: boat.model,
      currency: goal?.currency ?? boat.currency ?? 'EUR',
      targetRevenue: goal ? String(goal.targetRevenue) : '',
      targetWeeks: goal ? String(goal.targetWeeks) : '',
      notes: goal?.notes ?? '',
      hasGoal: !!goal,
      dirty: false,
    };
  }
  return next;
}

/**
 * Whether saving a draft means anything: any change to a saved goal, or a new
 * goal with a target. A new row touched and left empty would otherwise be
 * saved as a zero goal.
 */
export function isMeaningfulDraft(draft: GoalDraft): boolean {
  if (draft.hasGoal) return true;
  return Number(draft.targetRevenue) > 0 || Number(draft.targetWeeks) > 0;
}

/** The boats on the page with no goal this year and one last year: what "Copy" fills. */
export function boatsToCopyGoals(
  boatPids: string[],
  goalsThisYear: Pick<RevenueGoalResponse, 'marketBoatId'>[],
  goalsLastYear: Pick<RevenueGoalResponse, 'marketBoatId'>[]
): string[] {
  const hasGoal = new Set(goalsThisYear.map((g) => g.marketBoatId));
  const hadGoal = new Set(goalsLastYear.map((g) => g.marketBoatId));
  return boatPids.filter((pid) => !hasGoal.has(pid) && hadGoal.has(pid));
}
